import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import AdminSidebar from '../../components/admin/AdminSidebar';
import AdminMobileNav from '../../components/admin/AdminMobileNav';
import { Button } from '../../components/ui/button';
import { 
  Users, Calendar, DollarSign, TrendingUp, UserCheck, ChevronRight,
  RefreshCw, Activity, BarChart3, AlertTriangle, Tag, FileText, Shield, Image
} from 'lucide-react';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/reports/overview`);
      setOverview(response.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (isLoading && !overview) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 flex">
      <AdminSidebar />
      <AdminMobileNav />

      <main className="flex-1 p-4 md:p-8 md:ml-64 pt-16 md:pt-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-[Poppins]">Dashboard</h1>
            <p className="text-slate-500">Welcome back, {user?.name || 'Admin'}</p>
          </div>
          <Button variant="outline" onClick={fetchData} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* KPI Cards Row 1 */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-2xl p-4 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-sm text-slate-500">Total Users</span>
            </div>
            <p className="text-2xl font-bold text-slate-900">{overview?.users?.total || 0}</p>
            <p className="text-xs text-slate-400 mt-1">
              {overview?.users?.customers || 0} customers, {overview?.users?.providers || 0} providers
            </p>
          </div>
          
          <div className="bg-white rounded-2xl p-4 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-green-600" />
              </div>
              <span className="text-sm text-slate-500">Total Bookings</span>
            </div>
            <p className="text-2xl font-bold text-slate-900">{overview?.bookings?.total || 0}</p>
            <p className="text-xs text-slate-400 mt-1">
              {overview?.bookings?.completion_rate || 0}% completion rate
            </p>
          </div>
          
          <div className="bg-white rounded-2xl p-4 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-amber-600" />
              </div>
              <span className="text-sm text-slate-500">Total Revenue</span>
            </div>
            <p className="text-2xl font-bold text-slate-900">₱{(overview?.revenue?.total || 0).toLocaleString()}</p>
            <p className="text-xs text-slate-400 mt-1">
              ₱{(overview?.revenue?.avg_order_value || 0).toLocaleString()} avg order
            </p>
          </div>
          
          <div className="bg-white rounded-2xl p-4 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-purple-600" />
              </div>
              <span className="text-sm text-slate-500">Commission Earned</span>
            </div>
            <p className="text-2xl font-bold text-amber-600">₱{(overview?.revenue?.commission || 0).toLocaleString()}</p>
            <p className="text-xs text-slate-400 mt-1">
              ₱{(overview?.revenue?.monthly_commission || 0).toLocaleString()} this month
            </p>
          </div>
        </div>

        {/* KPI Cards Row 2 */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-2xl p-4 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <UserCheck className="w-5 h-5 text-green-500" />
              <span className="text-sm text-slate-500">Verified Providers</span>
            </div>
            <p className="text-xl font-bold text-slate-900">{overview?.users?.verified_providers || 0}</p>
          </div>
          
          <div className="bg-white rounded-2xl p-4 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <Activity className="w-5 h-5 text-blue-500" />
              <span className="text-sm text-slate-500">Active Bookings</span>
            </div>
            <p className="text-xl font-bold text-slate-900">{overview?.bookings?.active || 0}</p>
          </div>
          
          <div className="bg-white rounded-2xl p-4 shadow-sm">
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-5 h-5 text-pink-500" />
              <span className="text-sm text-slate-500">Referrals</span>
            </div>
            <p className="text-xl font-bold text-slate-900">{overview?.referrals?.total || 0}</p>
          </div>
          
          <div className="bg-white rounded-2xl p-4 shadow-sm bg-amber-50">
            <div className="flex items-center gap-3 mb-2">
              <UserCheck className="w-5 h-5 text-amber-600" />
              <span className="text-sm text-amber-800">Pending Actions</span>
            </div>
            <p className="text-xl font-bold text-amber-600">
              {(overview?.pending?.verifications || 0) + (overview?.pending?.payouts || 0)}
            </p>
          </div>
        </div>

        {/* Quick Actions */}
        <h2 className="text-lg font-bold text-slate-900 font-[Poppins] mb-4">Quick Actions</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          <button 
            className="bg-white rounded-2xl p-4 shadow-sm flex items-center justify-between hover:shadow-md transition-shadow"
            onClick={() => navigate('/admin/reports')}
            data-testid="quick-reports"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-blue-600" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-slate-900">Reports</p>
                <p className="text-xs text-slate-500">View analytics</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-400" />
          </button>
          
          <button 
            className="bg-white rounded-2xl p-4 shadow-sm flex items-center justify-between hover:shadow-md transition-shadow"
            onClick={() => navigate('/admin/providers')}
            data-testid="quick-verifications"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                <UserCheck className="w-5 h-5 text-amber-600" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-slate-900">Verifications</p>
                <p className="text-xs text-slate-500">{overview?.pending?.verifications || 0} pending</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-400" />
          </button>
          
          <button 
            className="bg-white rounded-2xl p-4 shadow-sm flex items-center justify-between hover:shadow-md transition-shadow"
            onClick={() => navigate('/admin/bookings')}
            data-testid="quick-bookings"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                <Calendar className="w-5 h-5 text-green-600" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-slate-900">Bookings</p>
                <p className="text-xs text-slate-500">Manage all</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-400" />
          </button>
          
          <button 
            className="bg-white rounded-2xl p-4 shadow-sm flex items-center justify-between hover:shadow-md transition-shadow"
            onClick={() => navigate('/admin/disputes')}
            data-testid="quick-disputes"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-slate-900">Disputes</p>
                <p className="text-xs text-slate-500">Review issues</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-400" />
          </button>

          <button 
            className="bg-white rounded-2xl p-4 shadow-sm flex items-center justify-between hover:shadow-md transition-shadow"
            onClick={() => navigate('/admin/insurance-claims')}
            data-testid="quick-insurance-claims"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <Shield className="w-5 h-5 text-blue-600" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-slate-900">Insurance Claims</p>
                <p className="text-xs text-slate-500">Manage claims</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-400" />
          </button>

          <button 
            className="bg-white rounded-2xl p-4 shadow-sm flex items-center justify-between hover:shadow-md transition-shadow"
            onClick={() => navigate('/admin/users')}
            data-testid="quick-users"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-slate-900">Users</p>
                <p className="text-xs text-slate-500">Manage accounts</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-400" />
          </button>
          
          <button 
            className="bg-white rounded-2xl p-4 shadow-sm flex items-center justify-between hover:shadow-md transition-shadow"
            onClick={() => navigate('/admin/promo-codes')}
            data-testid="quick-promos"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-pink-100 rounded-xl flex items-center justify-center">
                <Tag className="w-5 h-5 text-pink-600" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-slate-900">Promo Codes</p>
                <p className="text-xs text-slate-500">Manage discounts</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-400" />
          </button>

          <button 
            className="bg-white rounded-2xl p-4 shadow-sm flex items-center justify-between hover:shadow-md transition-shadow"
            onClick={() => navigate('/admin/banners')}
            data-testid="quick-banners"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
                <Image className="w-5 h-5 text-indigo-600" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-slate-900">Banners</p>
                <p className="text-xs text-slate-500">Ads & promotions</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-400" />
          </button>
          
          <button 
            className="bg-white rounded-2xl p-4 shadow-sm flex items-center justify-between hover:shadow-md transition-shadow"
            onClick={() => navigate('/admin/cms')}
            data-testid="quick-cms"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
                <FileText className="w-5 h-5 text-indigo-600" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-slate-900">CMS</p>
                <p className="text-xs text-slate-500">Edit content</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-400" />
          </button>
          
          <button 
            className="bg-white rounded-2xl p-4 shadow-sm flex items-center justify-between hover:shadow-md transition-shadow"
            onClick={() => navigate('/admin/settings')}
            data-testid="quick-settings"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-slate-600" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-slate-900">Settings</p>
                <p className="text-xs text-slate-500">API & Config</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-400" />
          </button>
        </div>
      </main>
    </div>
  );
}
